import {
  query,
  where,
  collection,
  getDocs,
  getDoc,
  addDoc,
  deleteDoc,
  serverTimestamp,
  doc,
  updateDoc,
  setDoc,
  increment,
} from "firebase/firestore";
import { firestore } from "../firebase-config";
import moment from "moment";
import * as apiService from "../api-service";

// EVENTS
// How far back to look by default when a caller doesn't need a tighter window (Booker, which lets
// someone navigate to arbitrary past/future weeks client-side over whatever's already loaded).
// Bookings this old have never been read by anything else in the app - this just stops a
// company's entire multi-year booking history from being re-downloaded in full on every Booker
// load, which only gets slower as that history grows, without cutting into any realistic amount
// of past navigation.
const DEFAULT_EVENTS_LOOKBACK_DAYS = 730;

// Get all events - bounded to ones that end on/after `cutoffDate` (defaulting to ~2 years back)
// rather than truly unbounded. A single inequality filter on date_end only (not also capping
// date_start for the future) needs no composite Firestore index, and never excludes a current or
// upcoming booking - only ones that are already long over drop out of the result set. Callers that
// only need a narrow window (e.g. Dashboard's "who's in today") can pass a tighter cutoffDate of
// their own for an even smaller, faster query.
export const getAllEvents = (companyId, cutoffDate) => {
  const effectiveCutoff = cutoffDate ?? moment().subtract(DEFAULT_EVENTS_LOOKBACK_DAYS, "days").toDate();
  return getDocs(
    query(collection(firestore, "companies", companyId, "events"), where("date_end", ">=", effectiveCutoff))
  );
};

export const requestNewHoliday = (
  userToken,
  companyData,
  companyId,
  title,
  dateStart,
  dateEnd,
  createdBy,
  employees,
  extraEmployeeIds
) => {
  return addDoc(collection(firestore, "companies", companyId, "events"), {
    title: title,
    date_start: moment(dateStart).startOf("day").toDate(),
    date_end: dateEnd ? moment(dateEnd).endOf("day").toDate() : moment(dateStart).startOf("day").toDate(),
    created_by: createdBy,
    state: 2,
    type: "holiday",
    approved_by: [],
    approve_by_extra: extraEmployeeIds,
    created: serverTimestamp(),
    updated: serverTimestamp(),
  }).then(() =>
    apiService.postSendEmailRaw({
      userIdToken: userToken,
      templateId: companyData["sendgrid"]["template_ids"]["holiday_request"],
      toEmails: [
        ...((companyData["employee_approvals_from"] ?? [])?.filter((e) => e.id === createdBy)[0]?.admins ?? []),
        extraEmployeeIds.map((id) => {
          return employees.filter((emp) => emp.uid === id)[0].email;
        })[0],
      ].map((admin) => {
        return employees
          .filter((em) => admin === em.uid)
          .map((a) => {
            return a.email;
          });
      })[0],
      ccEmails: companyData["emails_cc"],
      holidayStart: moment(dateStart).format("ddd, MMM D YYYY"),
      holidayEnd: dateEnd ? moment(dateEnd).format("ddd, MMM D YYYY") : moment(dateStart).format("ddd, MMM D YYYY"),
      fromName: title,
      fromEmail: companyData["sendgrid"]["from"],
      apiKey: companyData["sendgrid"]["key"],
    })
  );
};

export const approveHoliday = (
  userToken,
  companyData,
  companyId,
  documentId,
  dateStart,
  dateEnd,
  approvedBy,
  complete,
  employees,
  createdBy
) => {
  if (complete) {
    return updateDoc(doc(collection(firestore, "companies", companyId, "events"), documentId), {
      approved_by: approvedBy,
      state: 1,
      updated: serverTimestamp(),
    }).then(() =>
      apiService.postSendEmailRaw({
        userIdToken: userToken,
        templateId: companyData["sendgrid"]["template_ids"]["holiday_approved"],
        toEmails: (
          (companyData["employee_approvals_from"] ?? [])?.filter((e) => e.id === createdBy)[0]?.admins ?? []
        ).map((admin) => {
          return employees
            .filter((em) => admin === em.uid)
            .map((a) => {
              return a.email;
            });
        })[0],
        holidayStart: moment(dateStart).format("ddd, MMM D YYYY"),
        holidayEnd: dateEnd ? moment(dateEnd).format("ddd, MMM D YYYY") : moment(dateStart).format("ddd, MMM D YYYY"),
        ccEmails: companyData["emails_cc"],
        fromEmail: companyData["sendgrid"]["from"],
        apiKey: companyData["sendgrid"]["key"],
      })
    );
  } else {
    return updateDoc(doc(collection(firestore, "companies", companyId, "events"), documentId), {
      approved_by: approvedBy,
      updated: serverTimestamp(),
    });
  }
};
export const declineHoliday = (
  userToken,
  companyData,
  companyId,
  documentId,
  dateStart,
  dateEnd,
  currentUser,
  reason,
  employees,
  createdBy
) => {
  return updateDoc(doc(collection(firestore, "companies", companyId, "events"), documentId), {
    state: 0,
    declined_by: { id: currentUser.uid, name: currentUser.displayName, message: reason },
    updated: serverTimestamp(),
  }).then(() =>
    apiService.postSendEmailRaw({
      userIdToken: userToken,
      templateId: companyData["sendgrid"]["template_ids"]["holiday_declined"],
      toEmails: (
        (companyData["employee_approvals_from"] ?? [])?.filter((e) => e.id === createdBy)[0]?.admins ?? []
      ).map((admin) => {
        return employees
          .filter((em) => admin === em.uid)
          .map((a) => {
            return a.email;
          });
      })[0],
      holidayStart: moment(dateStart).format("ddd, MMM D YYYY"),
      holidayEnd: dateEnd ? moment(dateEnd).format("ddd, MMM D YYYY") : moment(dateStart).format("ddd, MMM D YYYY"),
      fromName: currentUser.displayName,
      declineReason: reason,
      ccEmails: companyData["emails_cc"],
      fromEmail: companyData["sendgrid"]["from"],
      apiKey: companyData["sendgrid"]["key"],
    })
  );
};

export const remindHoliday = (
  userToken,
  companyData,
  companyId,
  documentId,
  title,
  dateStart,
  dateEnd,
  employees,
  createdBy
) => {
  return updateDoc(doc(collection(firestore, "companies", companyId, "events"), documentId), {
    updated: serverTimestamp(),
  }).then(() =>
    apiService.postSendEmailRaw({
      userIdToken: userToken,
      templateId: companyData["sendgrid"]["template_ids"]["holiday_remind"],
      toEmails: (
        (companyData["employee_approvals_from"] ?? [])?.filter((e) => e.id === createdBy)[0]?.admins ?? []
      ).map((admin) => {
        return employees
          .filter((em) => admin === em.uid)
          .map((a) => {
            return a.email;
          });
      })[0],
      fromName: title,
      holidayStart: moment(dateStart).format("ddd, MMM D YYYY"),
      holidayEnd: dateEnd ? moment(dateEnd).format("ddd, MMM D YYYY") : moment(dateStart).format("ddd, MMM D YYYY"),
      ccEmails: companyData["emails_cc"],
      fromEmail: companyData["sendgrid"]["from"],
      apiKey: companyData["sendgrid"]["key"],
    })
  );
};

export const addNewParking = (companyId, title, dateStart, dateEnd, createdBy, spaceId, bookedByAdminName) => {
  var payload = {
    title: title,
    date_start: dateStart,
    date_end: dateEnd,
    created_by: createdBy,
    space: spaceId,
    type: "parking",
    created: serverTimestamp(),
  };

  if (bookedByAdminName.length > 0) {
    payload["created_by_admin_name"] = bookedByAdminName;
  }

  return addDoc(collection(firestore, "companies", companyId, "events"), payload);
};

// Meeting room bookings are time-slot based within a single day, same as parking - so this
// mirrors addNewParking exactly (raw start/end times, no day-level normalization), just against
// a meeting room id instead of a parking space id.
export const addNewMeetingRoom = (
  companyId,
  title,
  dateStart,
  dateEnd,
  createdBy,
  roomId,
  bookedByAdminName,
  // Admin/super only - a booking for someone who isn't a Portal user: `manual` stores no user id
  // (created_by: null) and `subtitle` is an optional free-text line shown under the name.
  { manual = false, subtitle = "" } = {}
) => {
  var payload = {
    title: title,
    ...(subtitle ? { subtitle } : {}),
    ...(manual ? { manual_booking: true } : {}),
    date_start: dateStart,
    date_end: dateEnd,
    created_by: manual ? null : createdBy,
    room: roomId,
    type: "meeting",
    created: serverTimestamp(),
  };

  if (bookedByAdminName.length > 0) {
    payload["created_by_admin_name"] = bookedByAdminName;
  }

  return addDoc(collection(firestore, "companies", companyId, "events"), payload);
};

// Encodes a desk booking's date range for a given time-of-day period. "AM"/"PM" use the exact
// same midnight/noon boundary convention as the Away/Off iCal feed (see getHalfDayPeriod in
// booker.js) - midnight-to-noon UTC for a morning booking, noon-to-next-midnight UTC for an
// afternoon one - so a half-day desk booking is picked up by that same "AM"/"PM" badge logic
// automatically, rather than needing a second, parallel half-day detection path.
//
// Deliberately built from dateStart's own LOCAL calendar-date components (year/month/day) via
// Date.UTC, rather than just reinterpreting the existing local Date instant in UTC (e.g.
// `moment.utc(dateStart)`) - the latter would shift the booking onto the wrong calendar day
// entirely whenever the browser's local timezone isn't UTC+0 (e.g. British Summer Time), since
// "local midnight on the 29th" and "UTC midnight on the 29th" are different instants. Full-day
// bookings are untouched - still anchored to local midnight/end-of-day exactly as before - since
// half-day-ness is the only thing that needs to line up with the (already UTC-anchored) Away/Off
// convention. Half days only make sense for a single day, so `period` is ignored (falls back to
// the existing full-day range) whenever a multi-day dateEnd is given.
const buildDeskDateRange = (dateStart, dateEnd, period) => {
  // dateStart arrives as a plain JS Date once someone has actually touched the date picker, but
  // as a moment object before that (desk.js's initial useState default) - moment(...) normalizes
  // either into a real moment instance, so .year()/.month()/.date() below work regardless of
  // which one was passed in. Calling native Date-only methods (.getFullYear() etc) directly on
  // dateStart, as an earlier version of this function did, threw "e.getFullYear is not a
  // function" the moment someone booked a half day without first touching the date picker.
  const startMoment = moment(dateStart);
  const startOfDay = startMoment.clone().startOf("day");
  const isSingleDay = !dateEnd || moment(dateEnd).isSame(dateStart, "day");

  if (isSingleDay && (period === "AM" || period === "PM")) {
    const y = startMoment.year();
    const m = startMoment.month();
    const d = startMoment.date();
    return period === "AM"
      ? { date_start: new Date(Date.UTC(y, m, d, 0, 0, 0)), date_end: new Date(Date.UTC(y, m, d, 12, 0, 0)) }
      : { date_start: new Date(Date.UTC(y, m, d, 12, 0, 0)), date_end: new Date(Date.UTC(y, m, d + 1, 0, 0, 0)) };
  }
  return {
    date_start: startOfDay.toDate(),
    date_end: dateEnd ? moment(dateEnd).endOf("day").toDate() : startOfDay.toDate(),
  };
};

export const addNewDesk = (
  companyId,
  title,
  dateStart,
  dateEnd,
  roomId,
  createdBy,
  createdAdmin,
  createdAdminName,
  period = "full",
  // Admin/super only - a booking for someone who isn't a Portal user: `manual` stores no user id
  // (created_by: null) and `subtitle` is an optional free-text line shown under the name.
  { manual = false, subtitle = "" } = {}
) => {
  const { date_start, date_end } = buildDeskDateRange(dateStart, dateEnd, period);
  if (createdAdmin && createdAdminName) {
    return addDoc(collection(firestore, "companies", companyId, "events"), {
      title: title,
      ...(subtitle ? { subtitle } : {}),
      ...(manual ? { manual_booking: true } : {}),
      date_start: date_start,
      date_end: date_end,
      created_by: manual ? null : createdBy,
      created_by_admin: createdAdmin,
      created_by_admin_name: createdAdminName,
      room: roomId,
      type: "desk",
      created: serverTimestamp(),
    });
  } else {
    return addDoc(collection(firestore, "companies", companyId, "events"), {
      title: title,
      date_start: date_start,
      date_end: date_end,
      created_by: createdBy,
      room: roomId,
      type: "desk",
      created: serverTimestamp(),
    });
  }
};

// Lets an existing desk booking's location be changed after the fact, without touching anything
// else about it (who it's for, which dates, AM/PM) - this only ever writes the `room` field (plus
// `updated`, matching every other event mutation in this file) rather than accepting a general
// partial-update payload. Deliberately kept separate from updateDeskBooking below (rather than
// having that one cover every case) - firestore.rules can specifically pin THIS exact {room,
// updated} diff down to admin/super or the booking's own creator, which it can't do for a diff
// that also includes date_start/date_end (too many other flows - trimming a day off a multi-day
// booking on cancel, etc - legitimately touch those same two fields). So a pure location edit,
// the common case, keeps the tightest possible server-side check; only switching the time-of-day
// (below) needs the broader, UI-enforced-only path every other date-touching update already uses.
export const updateDeskLocation = (companyId, documentId, roomId) => {
  return updateDoc(doc(collection(firestore, "companies", companyId, "events"), documentId), {
    room: roomId,
    updated: serverTimestamp(),
  });
};

// Lets an existing SINGLE-day desk booking's time-of-day (and, same as updateDeskLocation, its
// room) be changed after the fact - e.g. switching a full-day booking down to just a morning once
// a meeting's been scheduled for the afternoon. Reuses the exact same midnight/noon-boundary
// encoding addNewDesk uses (see buildDeskDateRange above) so the result is picked up by every
// existing AM/PM badge/half-day check without needing a parallel path. Only ever called when the
// period is actually changing (see desk-edit.js) - a pure room-only edit goes through
// updateDeskLocation instead; see the comment there for why the two are kept separate.
export const updateDeskBooking = (companyId, documentId, roomId, dateStart, period) => {
  const { date_start, date_end } = buildDeskDateRange(dateStart, null, period);
  return updateDoc(doc(collection(firestore, "companies", companyId, "events"), documentId), {
    room: roomId,
    date_start,
    date_end,
    updated: serverTimestamp(),
  });
};

export const addNewOffice = (
  companyId,
  title,
  dateStart,
  dateEnd,
  officeType,
  createdBy,
  createdAdmin,
  createdAdminName
) => {
  if (createdAdmin && createdAdminName) {
    return addDoc(collection(firestore, "companies", companyId, "events"), {
      title: title,
      date_start: moment(dateStart).startOf("day").toDate(),
      date_end: dateEnd ? moment(dateEnd).endOf("day").toDate() : moment(dateStart).startOf("day").toDate(),
      created_by: createdBy,
      created_by_admin: createdAdmin,
      created_by_admin_name: createdAdminName,
      office_type: officeType,
      type: "office",
      created: serverTimestamp(),
    });
  } else {
    return addDoc(collection(firestore, "companies", companyId, "events"), {
      title: title,
      date_start: moment(dateStart).startOf("day").toDate(),
      date_end: dateEnd ? moment(dateEnd).endOf("day").toDate() : moment(dateStart).startOf("day").toDate(),
      created_by: createdBy,
      office_type: officeType,
      type: "office",
      created: serverTimestamp(),
    });
  }
};

export const deleteEvent = (companyId, id) => {
  return deleteDoc(doc(collection(firestore, "companies", companyId, "events"), id));
};

// Cancels a booking for a single day within a (potentially multi-day) event, without touching the
// other days in the range. If the day being removed is the only day, the event is deleted outright.
// If it's the first or last day, the range is trimmed. If it's in the middle, the event is split into
// two separate bookings - one either side of the removed day - so the remaining days stay booked.
export const removeEventDay = (companyId, id, rawData, dayToRemove) => {
  // Parking and meeting-room bookings are a single time slot within a day (not a day-range like
  // Desk/Office), so the day-trim/split logic below doesn't apply to them - even if one happens to
  // cross midnight, cancelling it should always remove the whole booking outright rather than
  // trying to trim/split it by calendar day (which would otherwise corrupt it).
  if (rawData.type === "parking" || rawData.type === "meeting") {
    return deleteEvent(companyId, id);
  }

  const eventRef = doc(collection(firestore, "companies", companyId, "events"), id);
  const startMoment = moment(rawData.date_start.toDate());
  const endMoment = moment(rawData.date_end.toDate());
  const startDay = startMoment.clone().startOf("day");
  const endDay = endMoment.clone().startOf("day");
  const targetDay = moment(dayToRemove).startOf("day");

  if (startDay.isSame(endDay, "day") || targetDay.isBefore(startDay) || targetDay.isAfter(endDay)) {
    // Single-day booking (or the day isn't actually part of this event) - just remove it entirely.
    return deleteEvent(companyId, id);
  }

  if (targetDay.isSame(startDay, "day")) {
    // Removing the first day - trim the range to start the day after.
    return updateDoc(eventRef, { date_start: startDay.clone().add(1, "day").toDate() });
  }

  if (targetDay.isSame(endDay, "day")) {
    // Removing the last day - trim the range to end the day before.
    return updateDoc(eventRef, { date_end: endDay.clone().subtract(1, "day").endOf("day").toDate() });
  }

  // Removing a day from the middle of the range - split into two bookings either side of it.
  const { date_start, date_end, created, ...rest } = rawData;
  return updateDoc(eventRef, {
    date_end: targetDay.clone().subtract(1, "day").endOf("day").toDate(),
  }).then(() =>
    addDoc(collection(firestore, "companies", companyId, "events"), {
      ...rest,
      date_start: targetDay.clone().add(1, "day").toDate(),
      date_end: endMoment.toDate(),
      created: serverTimestamp(),
    })
  );
};

// POSTS
// Both the admin Posts page and the Dashboard read from this same query - the Dashboard
// additionally filters out any post still scheduled for a future publish date.
//
// This can't be a single unfiltered query. Firestore Security Rules only allow a query if its
// own where() clauses guarantee every possible result satisfies the rule - a rule of "readable if
// it's not a draft, or you wrote it" can never be proven safe for a plain query (some result
// could always be someone else's draft), so Firestore rejects the whole request outright rather
// than quietly filtering it. Splitting it into two queries that each independently satisfy one
// side of that same "or" - every non-draft post, plus every post (draft or not) this viewer wrote
// themselves - keeps both individually provable, then they're merged/de-duplicated and re-sorted
// here instead of relying on a single orderBy(). currentUserId is optional only so existing calls
// that predate this fix don't blow up - omitting it just means this viewer's own drafts (if any)
// won't come back, which is the same "everyone" view an unfiltered query used to give.
export const getPosts = async (companyId, currentUserId) => {
  const col = collection(firestore, "companies", companyId, "blog");

  const queries = [getDocs(query(col, where("is_draft", "==", false)))];
  if (currentUserId) {
    queries.push(getDocs(query(col, where("created_by", "==", currentUserId))));
  }
  const snapshots = await Promise.all(queries);

  const docsById = new Map();
  snapshots.forEach((snapshot) => snapshot.docs.forEach((d) => docsById.set(d.id, d)));

  const docs = Array.from(docsById.values()).sort((a, b) => {
    const aTime = a.data().created?.toMillis?.() ?? 0;
    const bTime = b.data().created?.toMillis?.() ?? 0;
    return bTime - aTime;
  });

  return { docs };
};

// Fetches a single post by id - used by PostDetail.js (the /post/:id route). Unlike getPosts
// above, this is a plain get() rather than a query, so the "queries are all or nothing" issue that
// forces getPosts to split into two where()-constrained requests doesn't apply here at all -
// Firestore evaluates a single get() against the rule per-document, so firestore.rules' own
// blog/{postId} match can simply reject this outright (a permission-denied rejection) if the
// viewer isn't allowed to see it (someone else's still-a-draft post), with no query gymnastics
// needed on this end to make that provable in advance.
export const getPost = (companyId, postId) => {
  return getDoc(doc(firestore, "companies", companyId, "blog", postId));
};

// POST VIEW ANALYTICS
// One doc per (post, viewer) - companies/{companyId}/blog/{postId}/views/{uid} - rather than an
// ever-growing log of every individual view, so opening the same post a dozen times over a month
// is still just one row in the admin-facing analytics panel (its view_count still reflects all of
// them). Only admins/super can ever read this subcollection (see firestore.rules) - anyone can
// write their OWN doc here, the same "strictly the user's own" shape already used by
// user_preferences.
const postViewDocRef = (companyId, postId, uid) =>
  doc(firestore, "companies", companyId, "blog", postId, "views", uid);

// Called once per PostDetail.js visit, only after that post has already been confirmed visible to
// this viewer (see isPostViewableByViewer there) - increment() safely starts a missing view_count
// at 1 rather than needing a separate "does this doc already exist" read first.
export const recordPostView = (companyId, postId, currentUser) => {
  return setDoc(
    postViewDocRef(companyId, postId, currentUser.uid),
    {
      viewer_name: currentUser.displayName ?? currentUser.email ?? "Unknown",
      last_viewed: serverTimestamp(),
      view_count: increment(1),
    },
    { merge: true }
  );
};

// Admin/super only - enforced by firestore.rules itself, not just hidden in the UI. Every
// viewer's own row for this post, most-recently-viewed first.
export const getPostViews = async (companyId, postId) => {
  const col = collection(firestore, "companies", companyId, "blog", postId, "views");
  const snapshot = await getDocs(col);
  return snapshot.docs
    .map((d) => ({ uid: d.id, ...d.data() }))
    .sort((a, b) => (b.last_viewed?.toMillis?.() ?? 0) - (a.last_viewed?.toMillis?.() ?? 0));
};

// Creating a new post - publishDate is optional and includes a specific time (not just a date);
// when set to a point in the future, the post exists in Firestore straight away (so it shows up
// in the admin list immediately) but stays hidden from the Dashboard until that moment arrives,
// letting an admin queue up a post ahead of time rather than having to remember to publish it
// later. audienceRoles is a list of extra roles (see EXTRA_ROLES in services/UserRoles.js) - the
// post is only shown to people holding at least one of them, and an empty list means everyone.
//
// isDraft is a separate, stronger form of "not visible yet" - a draft is hidden from the
// Dashboard no matter what publish_date says (even one already in the past), since it's meant
// for content that's still being written and hasn't been given a real publish date/time at all
// yet. Turning isDraft back off (from the same form, alongside picking/confirming a publish date)
// is what actually queues or publishes it.
// Asks the backend to check whether this specific post just went live and, if so, send the
// desktop notification - see functions/express/routes/notifications/notify-post-published.js.
// There's deliberately no Firestore-triggered Cloud Function doing this automatically (this
// project's Firestore database lives in a multi-region location that rules out a Gen 1 trigger,
// and Gen 2 Firestore triggers need a default Compute Engine service account this project doesn't
// have and can't provision - see functions/index.js's own comment for the full story), so this
// explicit call after every write is what actually catches an immediate publish. Fire-and-forget:
// never awaited by addPost/updatePost's own callers, and a failure here is only logged, never
// thrown - a notification-plumbing hiccup should never make the post save itself look like it
// failed. The backend's own scheduled sweep still independently covers a *scheduled* post's
// publish_date elapsing later, with nobody around to trigger this call at all.
const triggerPostPublishedCheck = async (companyId, postId, currentUser) => {
  try {
    const userIdToken = await currentUser.getIdToken();
    await apiService.notifyPostPublishedCheck({ userIdToken, companyId, postId });
  } catch (err) {
    console.error("Failed to trigger post-published notification check", err);
  }
};

// notifyOnPublish is whether a desktop push notification should go out (to the matching audience
// topic - see functions/express/utils/post-notifications.js) the moment this post actually goes
// live - decided once here while it's still a draft/not-yet-live, then frozen: PostForm.js stops
// showing/allowing changes to this once the post is actually live, and neither addPost nor
// updatePost is ever called again for a post that's already live in the first place other than
// through the form (Posts.js's separate "Publish Now" action calls updatePost directly with
// whatever was last saved here, never re-deciding it). `notified` is a backend-only idempotency
// guard - always starts false here and is only ever flipped to true by the backend once it's
// actually handled (sent or intentionally skipped) this post going live.
// What actually gets stored for a post's audience. `audience_roles` is the real list. `audience`
// is kept alongside it as the old single string so anything still reading it (an older cached copy
// of the app, mid-deploy) keeps behaving sensibly: "durian" is written for a Durian-only post
// exactly as before, "everyone" for no restriction, and "roles" for any other combination.
const audienceFields = (audienceRoles) => {
  const roles = [...new Set(audienceRoles ?? [])];
  const audience = roles.length === 0 ? "everyone" : roles.length === 1 && roles[0] === "durian" ? "durian" : "roles";
  return { audience, audience_roles: roles };
};

export const addPost = (
  companyId,
  { title, body, image, tags, audienceRoles, publishDate, isDraft, notifyOnPublish, currentUser }
) => {
  const col = collection(firestore, "companies", companyId, "blog");
  return addDoc(col, {
    title: title,
    body: body,
    image: image ?? null,
    tags: tags ?? [],
    ...audienceFields(audienceRoles),
    publish_date: publishDate ?? null,
    is_draft: isDraft ?? false,
    notify_on_publish: notifyOnPublish ?? true,
    notified: false,
    created: serverTimestamp(),
    created_by: currentUser.uid,
    created_by_name: currentUser.displayName,
  }).then((docRef) => {
    logActivity(companyId, currentUser, {
      action: isDraft ? "post_draft_created" : "post_created",
      targetType: "post",
      targetId: docRef.id,
      targetLabel: title,
    });
    triggerPostPublishedCheck(companyId, docRef.id, currentUser);
    return docRef;
  });
};

// Editing an existing post - tracked separately from created/created_by, so both who originally
// wrote a post and who most recently edited it stay visible.
export const updatePost = (
  companyId,
  postId,
  { title, body, image, tags, audienceRoles, publishDate, isDraft, notifyOnPublish, currentUser }
) => {
  const docRef = doc(firestore, "companies", companyId, "blog", postId);
  const data = {
    title: title,
    body: body,
    image: image ?? null,
    tags: tags ?? [],
    ...audienceFields(audienceRoles),
    publish_date: publishDate ?? null,
    is_draft: isDraft ?? false,
    updated: serverTimestamp(),
    updated_by: currentUser.uid,
    updated_by_name: currentUser.displayName,
  };
  // Only PostForm.js ever passes this explicitly, and only while the post isn't live yet -
  // Posts.js's "Publish Now" action calls updatePost directly without it, deliberately keeping
  // whatever was last saved via the form rather than re-deciding it at the moment of publishing
  // (matching "once published, the option is gone" - it was already locked in before that).
  // Omitted entirely (not defaulted) when absent, so it's never accidentally overwritten here.
  if (notifyOnPublish !== undefined) {
    data.notify_on_publish = notifyOnPublish;
  }
  return updateDoc(docRef, data).then(() => {
    logActivity(companyId, currentUser, {
      action: !isDraft && publishDate && moment(publishDate).isSameOrBefore(moment()) ? "post_published" : "post_updated",
      targetType: "post",
      targetId: postId,
      targetLabel: title,
    });
    triggerPostPublishedCheck(companyId, postId, currentUser);
  });
};

export const deletePost = (companyId, postId, title, currentUser) => {
  const docRef = doc(firestore, "companies", companyId, "blog", postId);
  return deleteDoc(docRef).then(() => {
    logActivity(companyId, currentUser, {
      action: "post_deleted",
      targetType: "post",
      targetId: postId,
      targetLabel: title,
    });
  });
};

// AUDIT LOG
// Append-only activity log - one entry per tracked action, written to
// companies/{companyId}/audit_log. Readable only by admin/super (see firestore.rules) and never
// updated or deleted afterward. actor_uid must match request.auth.uid on write (also enforced by
// the same rule), which is why this always takes the current signed-in user rather than a name/id
// passed in separately - it stops anyone logging an entry as someone else.
//
// Used from the various settings/write screens that only an admin would touch (company details,
// integrations, Booker room/parking config, Posts) - not for high-volume, low-signal actions like
// individual day-to-day bookings or Harvest time entries, which would just drown out anything
// worth actually reviewing.
//
// Best-effort: a logging failure should never block or roll back the action it's describing, so
// this swallows its own errors rather than rejecting - callers can fire it alongside their real
// write without needing their own try/catch around it.
export const logActivity = (companyId, currentUser, { action, targetType, targetId, targetLabel, summary }) => {
  if (!companyId || !currentUser?.uid || !action) {
    console.error("logActivity called with missing required fields", { companyId, action });
    return Promise.resolve();
  }

  return addDoc(collection(firestore, "companies", companyId, "audit_log"), {
    actor_uid: currentUser.uid,
    actor_name: currentUser.displayName ?? currentUser.email ?? "Unknown",
    actor_email: currentUser.email ?? "",
    action,
    target_type: targetType ?? null,
    target_id: targetId ?? null,
    target_label: targetLabel ?? null,
    summary: summary ?? "",
    created: serverTimestamp(),
  }).catch((err) => console.error("Failed to write audit log entry", { companyId, action, err }));
};
